import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import TeamHub from "@/pages/TeamHub";
import { MainLayout } from "@/components/layout/MainLayout";
import { readable, socialMeta } from "@/lib/routeSocial";
import { entityScripts } from "@/lib/entityJsonLd";
import { getEntityPicks } from "@/lib/publicPicks.functions";
import { ServerPicksList, picksJsonLd } from "@/components/predictions/ServerPicksList";

const teamQuery = (slug: string) =>
  queryOptions({
    queryKey: ["entity-picks", "team", slug],
    queryFn: () => getEntityPicks({ data: { field: "team", terms: [readable(slug)] } }),
    staleTime: 2 * 60_000,
  });

export const Route = createFileRoute("/teams/$slug")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(teamQuery(params.slug)),
  head: ({ params, loaderData }) => {
    const name = readable(params.slug);
    return {
      scripts: [
        ...entityScripts({ type: "SportsTeam", name, path: `/teams/${params.slug}`, crumbs: [{ name: "Teams", path: "/teams" }, { name, path: `/teams/${params.slug}` }], extra: { sport: "Association football" } }),
        ...(loaderData && loaderData.length ? [{ type: "application/ld+json", children: picksJsonLd(loaderData) }] : []),
      ],
      meta: socialMeta({
        seo: { topics: ["teams", "footballPredictions"], entities: [name] },
        title: `${name} Predictions & Betting Tips`,
        description: `${name} match predictions, value odds and betting tips backed by recent form and our 50-strategy analysis engine.`,
      }),
    };
  },
  component: TeamRoute,
  errorComponent: () => <MainLayout><TeamHub /></MainLayout>,
  notFoundComponent: () => <MainLayout><TeamHub /></MainLayout>,
});

function TeamRoute() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(teamQuery(slug));
  return (
    <MainLayout>
      <TeamHub />
      <div className="container mx-auto px-4"><ServerPicksList picks={data} /></div>
    </MainLayout>
  );
}
